import type { HTMLAttributes } from "react";
import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Redirect, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { featuresManager } from "../../../../shared/core/service/services";
import { ClientStatus, useClientDisplayName } from "../../../../shared/domains/client/client";
import { useObservable } from "../../../../shared/utils/observable";
import { usePromptPincodeChangeAfterAuthentication } from "../../../core/lifecycle/use-prompt-pincode-after-authentication";
import { PINCODE_MUST_BE_CHANGED_MODAL_ID } from "../../../core/modal/modal-id";
import { Path } from "../../../core/routing/path";
import { useClient } from "../../../domain/authentication/use-client";
import { useRTL } from "../../../domain/language/use-rtl";
import { OnboardingFileDelegateErrorStatusCard } from "../../screen/enrollment/onboarding/components/onboarding-file-delegate-error-status-card";
import { OnboardingFileStatusCard } from "../../screen/enrollment/onboarding/components/onboarding-file-status-card";
import { PincodeChangeModal } from "../../screen/pincode/pincode-change-modal";
import { StaticAssets } from "../../styles/custom-assets";
import { theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";
import { DefaultButton, DefaultLink } from "../buttons/default-button";
import { ErrorMessage } from "../error-message";
import { Modal } from "../modal/modal";
import { Nav } from "../nav/side-nav";
import { ResponsiveContainer } from "../responsive-container";
import { PageAlertsSlotContext } from "./page-alerts-slot";
import { PageHeaderBand, PageHeaderSubtitle, PageHeaderTitle } from "./page-header-band";

export interface PageWithNaveProps extends HTMLAttributes<HTMLDivElement> {
  withTitle?: boolean;
}

export function PageWithNav(props: PageWithNaveProps) {
  const { formatMessage } = useIntl();
  const { client, loading } = useClient();
  const location = useLocation();
  const { isRTL } = useRTL();
  const { withTitle = true } = props;
  const features = useObservable(featuresManager.features);

  usePromptPincodeChangeAfterAuthentication();
  const displayName = useClientDisplayName(client, true);

  // A screen may host the alerts itself by mounting a PageAlertsSlot; until one does, they stay
  // above the content, which is what every screen but the home does.
  const [alertsSlot, setAlertsSlot] = useState<HTMLElement | null>(null);
  const registerAlertsSlot = useCallback((node: HTMLElement | null) => setAlertsSlot(node), []);

  if (!loading && !client && features.clientInformationView) {
    return <Redirect to={{ pathname: Path.Root }} />;
  }
  const onOnboardingFilePage = location.pathname === Path.OnboardingFile;
  // The account and card pages carry their own content; the status cards belong to the home.
  const showAlerts = ![
    Path.MyAccount,
    Path.AccountStatements,
    Path.FeesStatements,
    Path.Cards,
    Path.AllCards,
    Path.AllTransactions,
    Path.JustifyTransaction,
    Path.ProfileMenu,
    Path.Profile,
    Path.Settings,
  ].includes(location.pathname as Path);

  const alerts = (
    <AlertContainer>
      {client?.status === ClientStatus.Pending || client?.status === ClientStatus.Initiated ? (
        <PendingCard>
          <CardIconImage src={StaticAssets.countdown} alt="" />
          <span>{formatMessage("clientStatus.accountUnderReview")}</span>
        </PendingCard>
      ) : null}
      {client?.status === ClientStatus.Blocked || client?.status === ClientStatus.Iniblocked ? (
        <ErrorMessageWrapper>
          <ErrorMessage>{formatMessage("clientStatus.clientBlockedMessage")}</ErrorMessage>
          {location.pathname !== Path.AccountBlocked && location.pathname !== Path.AccountBlockedAfterPincodeError && (
            <MoreLink to={Path.AccountBlocked} $isRTL={isRTL}>
              {formatMessage("clientStatus.clientBlockedLink")}
            </MoreLink>
          )}
        </ErrorMessageWrapper>
      ) : null}
      {client?.mustChangePincode ? (
        <InformationMessage>
          {formatMessage("pincodeChangeScreen.messageAfterAuthentication") + " "}
          <InlineButton
            onClick={() =>
              Modal.present(
                PINCODE_MUST_BE_CHANGED_MODAL_ID,
                () => <PincodeChangeModal type="after-authentication" />,
                {
                  canBeDismissed: false,
                },
              )
            }
          >
            {formatMessage("pincodeChangeScreen.changePincodeButton")}
          </InlineButton>
        </InformationMessage>
      ) : null}
      <OnboardingFileDelegateErrorStatusCard />
      <OnboardingFileStatusCard />
    </AlertContainer>
  );

  return (
    <Page>
      <>
        {!onOnboardingFilePage && <Nav />}
        <Content $withNav={!onOnboardingFilePage} $isRTL={isRTL}>
          <PageAlertsSlotContext.Provider value={registerAlertsSlot}>
            {!onOnboardingFilePage && (
              <>
                {withTitle && (
                  <PageHeaderBand>
                    <PageHeaderSubtitle>{formatMessage("connectedUserGreetings.hello")}</PageHeaderSubtitle>
                    {!!displayName && <PageHeaderTitle>{displayName}</PageHeaderTitle>}
                  </PageHeaderBand>
                )}
                {showAlerts && !alertsSlot && alerts}
              </>
            )}
            {props.children}
            {showAlerts && !onOnboardingFilePage && alertsSlot && createPortal(alerts, alertsSlot)}
          </PageAlertsSlotContext.Provider>
        </Content>
      </>
    </Page>
  );
}

/* Stretching lets the content column paint its background down to the bottom of the window. */
const Page = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 100vh;
  width: 100%;
  background-color: #fbfbfb;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex-direction: column;
  }
`;

const Content = styled(ResponsiveContainer)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 15vh;
  background-color: #fbfbfb;
`;

const CardIconImage = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const AlertContainer = styled.div<{ children?: React.ReactNode }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorMessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: -20px;
  margin-bottom: 40px;
`;

const MoreLink = styled(DefaultLink)<{ $isRTL: boolean }>`
  margin-left: ${(props) => (props.$isRTL ? 0 : 10)}px;
  margin-right: ${(props) => (props.$isRTL ? 10 : 0)}px;

  font-size: 0.875rem;
  ${theme.mediumText};
  text-decoration: underline;
  color: #000;
`;

const InlineButton = styled(DefaultButton)`
  display: inline-block;
  font-size: 0.875rem;
  ${theme.mediumText};
  padding: 0;
  text-decoration: underline;
`;

/* A neutral card in the side column, not the inline warning it used to be above the content. */
const InformationMessage = styled(ErrorMessage)`
  display: block;
  padding: 16px;
  border-radius: 13px;
  background-color: ${theme.colors.gray[100]};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.blue[800]};
`;

const PendingCard = styled(InformationMessage)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  ${theme.text};
  font-size: 14px;
  line-height: 19px;
  color: #000000;
`;
